import { createAsyncThunk, createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import moment from 'moment';
import { isNil } from 'lodash';

import { RootState } from 'app/store';
import { ApiResListsType, OhsApiRequestName } from 'global-services/api/OhsApiModels';
import globalModuleSearch from 'search/OhsSearchServices';
import {
  GlobalSearchState,
  OhsGlobalSearchFilter,
  OhsGlobalSearchPayload,
} from 'search/OhsSearchModels';
import { ModuleType, TierType } from 'global-services/constants/OhsObject';
import handleRegisterAsyncData, {
  handleRegisterCountAsyncData,
} from 'global-components/register/OhsModuleRegisterUtils';
import globalModuleSearchHistory from 'search/OhsSearchHistoryServices';
import OhsFetchAll from 'global-services/OhsFetchAll';
import { OhsUser } from 'user/OhsUser';
import OhsUserLocalServices from 'user/OhsUserLocalServices';

import {
  AllocatedListType,
  OhsInspectionFilterPayload,
  OhsAvailableInspectionFilterPayload,
  OhsInspectionViewPresets,
  OhsInspectionRootState,
  OhsInspectionHistoryFilterPayload,
} from './OhsInspectionModels';
import OhsInspectionRecord from './models/OhsInspectionRecord';
import {
  getAvailableInspectionList,
  getAvailableInspectionListTask,
  getInspectionAllocatedList,
  getInspectionCopyList,
  getInspectionHistoryList,
  getInspectionList,
  getInspectionListWithAllocations,
  getInspectionScheduleList,
} from './OhsInspectionServices';

enum RPCTypes {
  List = 'list',
  Scheduled = 'scheduled',
  History = 'history',
}

export interface InspectionListRecordState {
  isLoading: boolean;
  inspectionList: ApiResListsType<any[]> | null;
  inspectionScheduleList: ApiResListsType<any[]> | null;
  inspectionHistoryList: ApiResListsType<any[]> | null;
  allocatedList: AllocatedListType[] | null;
  inspectionCopyList: {
    isLoading: boolean;
    copyList: OhsInspectionRecord[];
  };
  availableInspectionList: ApiResListsType<any[]> | null;
  currentPage: number;
  currentViewPreset: OhsInspectionViewPresets;
  availableInspectionSearch: GlobalSearchState;
}

export const defaultInspectionAvailableSearch: OhsGlobalSearchFilter = {
  page: 1,
  count: false,
  modules: [],
  skipOrgRecords: false,
  workplaces: [],
  task: {
    complete: false,
    includeMasterRecords: false,
  },
  skipAllocatedRecords: false,
  archived: false,
};

const initialInspectionSearchPayload: OhsGlobalSearchPayload = {
  searchKey: '',
  substringSearch: true,
  highlight: false,
  filter: defaultInspectionAvailableSearch,
};

const initSearchResults = {
  items: [],
  pagination: {
    page: 1,
    totalPages: 1,
    next: '',
  },
};

const initialState: InspectionListRecordState = {
  isLoading: false,
  inspectionList: null,
  inspectionScheduleList: null,
  inspectionHistoryList: null,
  allocatedList: null,
  inspectionCopyList: {
    isLoading: false,
    copyList: [],
  },
  availableInspectionList: null,
  availableInspectionSearch: {
    isLoading: false,
    searchInfo: initialInspectionSearchPayload,
    searchResults: initSearchResults,
    currentPage: 1,
  },
  currentPage: 1,
  currentViewPreset: OhsInspectionViewPresets.view2InspectionScheduledList,
};

export const fetchInspectionAllocatedListAsync = createAsyncThunk<
  AllocatedListType[],
  { _ids: string[] },
  { state: RootState }
>('inspection/fetchInspectionAllocatedList', async (inspectionIDs, thunkAPI): Promise<any> => {
  try {
    const response = await getInspectionAllocatedList(inspectionIDs._ids);
    const resVal = response;
    return resVal;
  } catch (err: any) {
    const error: AxiosError<any> = err; // cast the error for access
    if (!error.response) {
      throw err;
    }

    return thunkAPI.rejectWithValue(err.response.data);
  }
});

export const inspectionSearch = async (
  searchDetails: any,
  apiRequestName?: OhsApiRequestName
): Promise<ApiResListsType<OhsInspectionRecord[]> | null> => {
  const globalSearchRes: any = await globalModuleSearch(searchDetails, apiRequestName);
  return globalSearchRes.result;
};

export const inspectionSearchHistory = async (
  searchDetails: any
): Promise<ApiResListsType<OhsInspectionRecord[]> | null> => {
  const globalSearchRes: any = await globalModuleSearchHistory(searchDetails);
  return globalSearchRes.result;
};

const inspectionListRPC = async ({
  inspectionState,
  inspectionFilters,
  type,
  count,
  orgAdminTierId,
  inspectionId,
}: {
  inspectionState: OhsInspectionRootState;
  inspectionFilters: OhsInspectionFilterPayload;
  type: RPCTypes;
  count: boolean;
  orgAdminTierId?: string;
  inspectionId?: string;
}): Promise<ApiResListsType<any[]> | null> => {
  const { globalSearch } = inspectionState;
  const user = OhsUserLocalServices.getLocalOhsUser() as OhsUser;
  const userTier = user?.tierNum ?? 0;
  const hasGlobalSearch = inspectionState.globalSearch.searchInfo.searchKey !== '';

  const getSearchFilter = () => {
    const { filter } = globalSearch.searchInfo;
    if (type === RPCTypes.Scheduled) {
      return {
        ...globalSearch.searchInfo,
        filter: {
          skipOrgRecords: filter.skipOrgRecords,
          workplaces: filter.workplaces,
          page: filter.page,
          count,
          archived: filter.archived,
        },
      };
    }
    if (type === RPCTypes.List && user?.tier?.type === TierType.T3) {
      return {
        ...globalSearch.searchInfo,
        filter: { ...filter, count, skipAllocatedRecords: true },
      };
    }
    if (type === RPCTypes.History) {
      const payload = {
        ...globalSearch.searchInfo,
        filter: {
          _id: inspectionId,
          type: ModuleType.Inspection,
          page: filter.page,
          count,
          workplaces: filter.workplaces,
          skipOrgRecords: filter.skipOrgRecords,
        },
        sort: globalSearch.searchInfo.sort && JSON.parse(globalSearch.searchInfo.sort),
      };
      if (!globalSearch.searchInfo.sort) {
        delete payload.sort;
      }

      return payload;
    }

    return { ...globalSearch.searchInfo, filter: { ...filter, count } };
  };

  const withViewPermission = user?.configs.inspection?.PERMISSIONS.view;

  if (userTier) {
    if (userTier === 1 && orgAdminTierId) {
      const response = getInspectionList(inspectionFilters, orgAdminTierId);
      return response;
    }

    if (withViewPermission) {
      if (type === RPCTypes.Scheduled) {
        if (userTier === 3 || userTier === 4) {
          return hasGlobalSearch
            ? inspectionSearch(getSearchFilter(), OhsApiRequestName.InspectionScheduleSearch)
            : getInspectionScheduleList(inspectionFilters);
        }
      } else if (type === RPCTypes.History) {
        return hasGlobalSearch
          ? inspectionSearchHistory(getSearchFilter())
          : getInspectionHistoryList(inspectionFilters, inspectionId || '');
      } else {
        if (userTier === 2) {
          return hasGlobalSearch
            ? inspectionSearch(getSearchFilter())
            : getInspectionList(inspectionFilters);
        }
        if (userTier === 3) {
          return getInspectionListWithAllocations(inspectionState, inspectionFilters, () =>
            inspectionSearch(getSearchFilter())
          );
        }
        if (userTier === 4) {
          return hasGlobalSearch
            ? inspectionSearch(getSearchFilter())
            : getInspectionList(inspectionFilters);
        }
      }
    }
  }
  return null;
};

// TEMPLATE
export const fetchInspectionListAsync = createAsyncThunk<
  ApiResListsType<OhsInspectionRecord[]> | null,
  string | undefined
>('inspection/fetchInspectionList', async (orgAdminTierId: string | undefined, thunkAPI) => {
  const {
    inspection,
    globalfilter: { filterInfo },
  } = thunkAPI.getState() as OhsInspectionRootState;

  const inspectionState = thunkAPI.getState() as OhsInspectionRootState;
  const data = inspection.inspectionLists;
  const { currentPage, currentViewPreset } = inspection;
  const viewPresetFilter = filterInfo.inspectionModule[currentViewPreset];
  const inspectionFilters: OhsInspectionFilterPayload = {
    ...viewPresetFilter,
    next: currentPage > 10 ? data?.pagination?.next ?? '' : '',
    page: currentPage > 10 ? 0 : currentPage,
    sort: { ...JSON.parse(String(filterInfo.inspectionModule[currentViewPreset].sort)) },
    count: false,
  };

  try {
    const response = inspectionListRPC({
      inspectionState,
      inspectionFilters,
      type: RPCTypes.List,
      count: false,
      orgAdminTierId,
    });
    return await response;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(err.response.data);
  }
});
export const fetchInspectionListCountAsync = createAsyncThunk<
  ApiResListsType<OhsInspectionRecord[]> | null,
  string | undefined
>('inspection/fetchInspectionCountList', async (orgAdminTierId: string | undefined, thunkAPI) => {
  const {
    inspection,
    globalfilter: { filterInfo },
  } = thunkAPI.getState() as OhsInspectionRootState;

  const inspectionState = thunkAPI.getState() as OhsInspectionRootState;
  const { currentViewPreset } = inspection;
  const viewPresetFilter = filterInfo.inspectionModule[currentViewPreset];

  const inspectionFilters: OhsInspectionFilterPayload = {
    ...viewPresetFilter,
    count: true,
    page: 1,
    sort: { order: 1, key: '_id' },
  };

  try {
    const response = await inspectionListRPC({
      inspectionState,
      inspectionFilters,
      type: RPCTypes.List,
      count: true,
      orgAdminTierId,
    });
    return response;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(err.response.data);
  }
});

// SCHEDULE
export const fetchInspectionScheduleListAsync = createAsyncThunk<
  ApiResListsType<OhsInspectionRecord[]> | null,
  undefined
>('inspection/fetchInspectionScheduleList', async (_, thunkAPI) => {
  const {
    inspection,
    globalfilter: { filterInfo },
  } = thunkAPI.getState() as OhsInspectionRootState;
  const inspectionState = thunkAPI.getState() as OhsInspectionRootState;

  const data = inspection.inspectionLists;
  const { currentPage, currentViewPreset } = inspection;
  const viewPresetFilter = filterInfo.inspectionModule[currentViewPreset];

  const inspectionFilters: OhsInspectionFilterPayload = {
    ...viewPresetFilter,
    dateDue: filterInfo.inspectionModule.dateDue
      ? {
          start: moment(new Date(filterInfo.inspectionModule.dateDue.start)).format('YYYY-MM-DD'),
          end: moment(new Date(filterInfo.inspectionModule.dateDue.end)).format('YYYY-MM-DD'),
        }
      : undefined,
    next: currentPage > 10 ? data?.pagination?.next ?? '' : '',
    page: currentPage > 10 ? 0 : currentPage,
    sort: { ...JSON.parse(String(filterInfo.inspectionModule[currentViewPreset].sort)) },
    count: false,
  };

  try {
    const response = await inspectionListRPC({
      inspectionState,
      inspectionFilters,
      type: RPCTypes.Scheduled,
      count: false,
    });
    return response;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(err.response.data);
  }
});
export const fetchInspectionScheduleListCountAsync = createAsyncThunk<
  ApiResListsType<OhsInspectionRecord[]> | null,
  undefined
>('inspection/fetchInspectionScheduleCountList', async (_, thunkAPI) => {
  const {
    inspection,
    globalfilter: { filterInfo },
  } = thunkAPI.getState() as OhsInspectionRootState;

  const inspectionState = thunkAPI.getState() as OhsInspectionRootState;
  const { currentViewPreset } = inspection;
  const viewPresetFilter = filterInfo.inspectionModule[currentViewPreset];

  const inspectionFilters: OhsInspectionFilterPayload = {
    ...viewPresetFilter,
    dateDue: filterInfo.inspectionModule.dateDue
      ? {
          start: moment(new Date(filterInfo.inspectionModule.dateDue.start)).format('YYYY-MM-DD'),
          end: moment(new Date(filterInfo.inspectionModule.dateDue.end)).format('YYYY-MM-DD'),
        }
      : undefined,
    count: true,
    page: 1,
    sort: { order: 1, key: '_id' },
  };

  try {
    const response = await inspectionListRPC({
      inspectionState,
      inspectionFilters,
      type: RPCTypes.Scheduled,
      count: true,
    });
    return response;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(err.response.data);
  }
});

// HISTORY
export const fetchInspectionHistoryListAsync = createAsyncThunk<
  ApiResListsType<OhsInspectionRecord[]> | null,
  string
>('inspection/fetchInspectionHistoryList', async (inspectionId, thunkAPI) => {
  const {
    inspection,
    globalfilter: { filterInfo },
  } = thunkAPI.getState() as OhsInspectionRootState;
  const inspectionState = thunkAPI.getState() as OhsInspectionRootState;

  const data = inspection.inspectionLists;
  const { currentPage, currentViewPreset } = inspection;
  const viewPresetFilter = filterInfo.inspectionModule[currentViewPreset];

  const inspectionFilters: OhsInspectionHistoryFilterPayload = {
    ...viewPresetFilter,
    dateCompleted: filterInfo.inspectionModule.dateCompleted
      ? {
          start: moment(new Date(filterInfo.inspectionModule.dateCompleted.start)).format(
            'YYYY-MM-DD'
          ),
          end: moment(new Date(filterInfo.inspectionModule.dateCompleted.end)).format('YYYY-MM-DD'),
        }
      : undefined,
    next: currentPage > 10 ? data?.pagination?.next ?? '' : '',
    page: currentPage > 10 ? 0 : currentPage,
    sort: { ...JSON.parse(String(filterInfo.inspectionModule[currentViewPreset].sort)) },
    count: false,
  };

  try {
    const response = await inspectionListRPC({
      inspectionState,
      inspectionFilters,
      type: RPCTypes.History,
      count: false,
      inspectionId,
    });
    return response;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(err.response.data);
  }
});
export const fetchInspectionHistoryListCountAsync = createAsyncThunk<
  ApiResListsType<OhsInspectionRecord[]> | null,
  string
>('inspection/fetchInspectionHistoryCountList', async (inspectionId, thunkAPI) => {
  const {
    inspection,
    globalfilter: { filterInfo },
  } = thunkAPI.getState() as OhsInspectionRootState;

  const inspectionState = thunkAPI.getState() as OhsInspectionRootState;
  const { currentViewPreset } = inspection;
  const viewPresetFilter = filterInfo.inspectionModule[currentViewPreset];

  const inspectionFilters: OhsInspectionHistoryFilterPayload = {
    ...viewPresetFilter,
    dateCompleted: filterInfo.inspectionModule.dateCompleted
      ? {
          start: moment(new Date(filterInfo.inspectionModule.dateCompleted.start)).format(
            'YYYY-MM-DD'
          ),
          end: moment(new Date(filterInfo.inspectionModule.dateCompleted.end)).format('YYYY-MM-DD'),
        }
      : undefined,
    count: true,
    page: 1,
    sort: { ...JSON.parse(String(filterInfo.inspectionModule[currentViewPreset].sort)) },
  };

  try {
    const response = await inspectionListRPC({
      inspectionState,
      inspectionFilters,
      type: RPCTypes.History,
      count: true,
      inspectionId,
    });
    return response;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(err.response.data);
  }
});

// COPY
export const fetchInspectionCopyListAsync = createAsyncThunk<
  ApiResListsType<any[]>,
  undefined,
  { state: RootState }
>(
  'inspection/fetchInspectionCopyList',
  async (inspectionIDs, thunkAPI): Promise<ApiResListsType<any[]> | any> => {
    try {
      const response = await OhsFetchAll(getInspectionCopyList, {
        archived: false,
        sort: {
          order: 1,
          key: '_id',
        },
      });
      const resVal = response;
      return resVal;
    } catch (err: any) {
      const error: AxiosError<any> = err; // cast the error for access
      if (!error.response) {
        throw err;
      }

      return thunkAPI.rejectWithValue(err.response.data);
    }
  }
);

// AVAILABLE
export const fetchAvailableInspectionListCountAsync = createAsyncThunk<
  ApiResListsType<OhsInspectionRecord[]> | null,
  string | undefined
>('inspection/fetchAvailableInspectionListCountAsync', async (_, thunkAPI: any) => {
  const { inspection } = thunkAPI.getState() as OhsInspectionRootState;
  const { currentPage } = inspection;

  const inspectionAvailableSearch = inspection.availableInspectionSearch;

  const setInspectionFilters: OhsAvailableInspectionFilterPayload = {
    page: currentPage > 10 ? 0 : currentPage,

    sort: { key: '_id', order: 1 },
    count: true,
  };

  const searchInfo = {
    searchKey: inspectionAvailableSearch.searchInfo.searchKey,
    substringSearch: inspectionAvailableSearch.searchInfo.substringSearch ?? false,
    highlight: inspectionAvailableSearch.searchInfo.highlight ?? false,
    filter: { count: true, page: inspectionAvailableSearch?.searchInfo?.filter?.page },
  };

  try {
    const response =
      inspectionAvailableSearch?.searchInfo?.searchKey !== ''
        ? await inspectionSearch(searchInfo, OhsApiRequestName.InspectionAvailableSearch)
        : await getAvailableInspectionList(setInspectionFilters);

    return response;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(err.response.data);
  }
});
export const fetchAvailableInspectionListAsync = createAsyncThunk<
  ApiResListsType<OhsInspectionRecord[]> | null,
  undefined
>('inspection/fetchAvailableInspectionListAsync', async (_, thunkAPI: any) => {
  const { inspection } = thunkAPI.getState() as OhsInspectionRootState;
  const { currentPage } = inspection;
  const inspectionAvailableSearch: any = inspection.availableInspectionSearch;

  const setInspectionFilters: OhsAvailableInspectionFilterPayload = {
    page: currentPage > 10 ? 0 : currentPage,

    sort: { ...JSON.parse(String('{ "key": "title", "order": 1 }')) },
    count: false,
  };

  const searchInfo = {
    searchKey: inspectionAvailableSearch.searchInfo.searchKey,
    substringSearch: inspectionAvailableSearch.searchInfo.substringSearch ?? false,
    highlight: inspectionAvailableSearch.searchInfo.highlight ?? false,
    filter: { count: false, page: inspectionAvailableSearch?.searchInfo?.filter?.page },
  };

  try {
    const response =
      inspectionAvailableSearch?.searchInfo?.searchKey !== ''
        ? await inspectionSearch(searchInfo, OhsApiRequestName.InspectionAvailableSearch)
        : await getAvailableInspectionList(setInspectionFilters);

    try {
      // call separate api to include inexecution prop in inspection record
      const recurringInspections = (response?.items || []).filter(
        (item) => !isNil(item.frequencyKey)
      );
      const recurringIds = recurringInspections.map((item) => item._id);
      const taskList = await getAvailableInspectionListTask(recurringIds);

      const taskObj: any = {};
      (taskList || []).forEach((task) => {
        taskObj[task.for._id] = task;
      });

      const formattedItems = (response?.items || []).map((item) => {
        const task = taskObj[item._id];
        if (task) {
          return { ...item, execute: { task } };
        }
        return item;
      });

      return {
        ...response,
        items: formattedItems,
      };
    } catch (error) {
      console.error(error);
      return response;
    }
  } catch (err: any) {
    return thunkAPI.rejectWithValue(err.response.data);
  }
});

export const inspectionSlice = createSlice({
  name: 'inspection',
  initialState,
  reducers: {
    setCurrentRegisterPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setInspectionIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setViewPreset: (state, action: PayloadAction<OhsInspectionViewPresets>) => {
      state.currentViewPreset = action.payload;
    },
    setAvailableInspectionSearch: (state, action: PayloadAction<OhsGlobalSearchPayload>) => {
      state.availableInspectionSearch.searchInfo = action.payload;
    },
    clearAvailableInspectionSearch: (state) => {
      state.availableInspectionSearch.searchInfo =
        initialState.availableInspectionSearch.searchInfo;
    },
    setAvailableInspectionSearchCurrentPage: (state, action: PayloadAction<number>) => {
      state.availableInspectionSearch.currentPage = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // TEMPLATE
      .addCase(fetchInspectionListAsync.pending, (state) => {
        state.isLoading = true;
        state.inspectionList = {
          items: [],
          pagination: state.inspectionList?.pagination ?? {
            page: 0,
            totalPages: 0,
            next: '',
          },
        };
      })
      .addCase(fetchInspectionListAsync.fulfilled, (state, action) => {
        if (action.payload) {
          const inspectionResult = action.payload;
          const inspectionList = handleRegisterAsyncData(
            inspectionResult,
            'inspectionListTotalPages'
          );
          state.isLoading = false;
          state.inspectionList = inspectionList;
        }
      })
      .addCase(fetchInspectionListAsync.rejected, (state) => {
        state.inspectionList = null;
      })
      .addCase(fetchInspectionListCountAsync.fulfilled, (state, action) => {
        if (state.inspectionList && action.payload) {
          const countInspectionList = handleRegisterCountAsyncData(
            action.payload,
            state.inspectionList,
            'inspectionListTotalPages'
          );
          state.inspectionList = countInspectionList;
        }
      })

      // SCHEDULE
      .addCase(fetchInspectionScheduleListAsync.pending, (state) => {
        state.isLoading = true;
        state.inspectionScheduleList = {
          items: [],
          pagination: state.inspectionScheduleList?.pagination ?? {
            page: 0,
            totalPages: 0,
            next: '',
          },
        };
      })
      .addCase(fetchInspectionScheduleListAsync.fulfilled, (state, action) => {
        if (action.payload) {
          const inspectionScheduleResult = action.payload;
          const inspectionScheduleList = handleRegisterAsyncData(
            inspectionScheduleResult,
            'inspectionScheduleListTotalPages'
          );
          state.isLoading = false;
          state.inspectionScheduleList = inspectionScheduleList;
        }
      })
      .addCase(fetchInspectionScheduleListAsync.rejected, (state) => {
        state.inspectionScheduleList = null;
      })
      .addCase(fetchInspectionScheduleListCountAsync.fulfilled, (state, action) => {
        if (state.inspectionScheduleList && action.payload) {
          const countInspectionScheduleList = handleRegisterCountAsyncData(
            action.payload,
            state.inspectionScheduleList,
            'inspectionScheduleListTotalPages'
          );
          state.inspectionScheduleList = countInspectionScheduleList;
          state.isLoading = false;
        }
      })

      // HISTORY
      .addCase(fetchInspectionHistoryListAsync.pending, (state) => {
        state.isLoading = true;
        state.inspectionHistoryList = {
          items: [],
          pagination: state.inspectionHistoryList?.pagination ?? {
            page: 0,
            totalPages: 0,
            next: '',
          },
        };
      })
      .addCase(fetchInspectionHistoryListAsync.fulfilled, (state, action) => {
        if (action.payload) {
          const inspectionScheduleResult = action.payload;
          const inspectionHistoryList = handleRegisterAsyncData(
            inspectionScheduleResult,
            'inspectionHistoryListTotalPages'
          );
          state.isLoading = false;
          state.inspectionHistoryList = inspectionHistoryList;
        }
      })
      .addCase(fetchInspectionHistoryListAsync.rejected, (state) => {
        state.inspectionHistoryList = null;
      })
      .addCase(fetchInspectionHistoryListCountAsync.fulfilled, (state, action) => {
        if (state.inspectionHistoryList && action.payload) {
          const countInspectionHistoryList = handleRegisterCountAsyncData(
            action.payload,
            state.inspectionHistoryList,
            'inspectionHistoryListTotalPages'
          );
          state.inspectionHistoryList = countInspectionHistoryList;
          state.isLoading = false;
        }
      })

      // ALLOCATED
      .addCase(fetchInspectionAllocatedListAsync.pending, (state) => {
        state.allocatedList = [];
      })
      .addCase(fetchInspectionAllocatedListAsync.fulfilled, (state, action) => {
        state.allocatedList = action.payload ?? [];
        state.isLoading = false;
      })

      // COPY
      .addCase(fetchInspectionCopyListAsync.pending, (state) => {
        state.inspectionCopyList.copyList = [];
        state.inspectionCopyList.isLoading = true;
      })
      .addCase(fetchInspectionCopyListAsync.fulfilled, (state, action) => {
        state.inspectionCopyList.copyList = action.payload.items ?? [];
        state.inspectionCopyList.isLoading = false;
      })

      // AVAILABLE
      .addCase(fetchAvailableInspectionListCountAsync.fulfilled, (state, action) => {
        if (state.availableInspectionList && action.payload) {
          const countAvailableInspectionList = handleRegisterCountAsyncData(
            action.payload,
            state.availableInspectionList,
            'availableInspectionListTotalPages'
          );
          state.availableInspectionList = countAvailableInspectionList;
          state.isLoading = false;
        }
      })
      .addCase(fetchAvailableInspectionListAsync.fulfilled, (state, action) => {
        if (action.payload) {
          const availableInspectionList = handleRegisterAsyncData(
            action.payload,
            'availableInspectionListTotalPages'
          );
          state.isLoading = false;
          state.availableInspectionList = availableInspectionList;
        }
      })
      .addCase(fetchAvailableInspectionListAsync.rejected, (state) => {
        state.availableInspectionList = null;
      });
  },
});
// Memoized Selectors
const inspectionState = (state: RootState) => state.inspection;
export const getOhsInspectionList = createSelector([inspectionState], (inspection) => inspection);
export const getAvailableInspectionSearch = createSelector(
  [inspectionState],
  (inspection) => inspection.availableInspectionSearch
);

export const {
  setCurrentRegisterPage,
  setInspectionIsLoading,
  setViewPreset,
  setAvailableInspectionSearch,
  clearAvailableInspectionSearch,
  setAvailableInspectionSearchCurrentPage,
} = inspectionSlice.actions;
export const inspectionModuleReducer = inspectionSlice.reducer;
